import { Reorder, useDragControls } from "framer-motion"
import {
  type ComponentType,
  type CSSProperties,
  type PointerEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import isEqual from "react-fast-compare"

type ItemID = string | number

export const keepDisabledItemsInPlace = (
  nextOrder: ItemID[],
  originalOrder: ItemID[],
  disabledItemIDs: ItemID[]
): ItemID[] => {
  const disabledIDs = new Set(disabledItemIDs)
  const movableIDs = nextOrder.filter((itemID) => !disabledIDs.has(itemID))

  return originalOrder.map((itemID) =>
    disabledIDs.has(itemID) ? itemID : movableIDs.shift()!
  )
}

type SortResult<Item> = {
  oldIndex: number
  newIndex: number
  items: Item[]
}

type DragHandleProps = {
  children: (props: {
    onPointerDown: PointerEventHandler<HTMLElement>
    canOpen: () => boolean
  }) => ReactNode
}

type RenderItemProps<Item> = {
  onRemove: () => void
  onUpdate: (update: unknown) => void
  onSetActive: () => void
  item: Item
  isAtMin: boolean
  isActive: boolean
  index: number
  DragHandle: ComponentType<DragHandleProps>
}

type MotionReorderProps<Item> = {
  items: Item[]
  itemOrderIds: ItemID[]
  activeIndex: number
  disabledIndices: number[]
  autoGenerateItem?: boolean
  checkIsAutoGenerated?: (item: Item) => boolean
  isAtMin: boolean
  itemContainerStyles?: CSSProperties
  renderItem: (props: RenderItemProps<Item>) => ReactNode
  onRemove: (index: number) => void
  onUpdate: (index: number) => (update: unknown) => void
  onSetActiveIndex: (index: number) => void
  onSort: (result: SortResult<Item>) => void
  children?: ReactNode
}

type MotionReorderItemProps<Item> = Pick<
  MotionReorderProps<Item>,
  "isAtMin" | "renderItem" | "onRemove" | "onSetActiveIndex"
> & {
  itemID: ItemID
  item: Item
  index: number
  isActive: boolean
  isDisabled: boolean
  handleUpdateItem: MotionReorderProps<Item>["onUpdate"]
  onDragStart: (itemID: ItemID) => void
  onDragEnd: () => void
}

const useMotionReorder = <Item,>({
  items,
  itemOrderIds,
  disabledItemIDs,
  onSort
}: Pick<MotionReorderProps<Item>, "items" | "itemOrderIds" | "onSort"> & {
  disabledItemIDs: ItemID[]
}) => {
  const [order, setOrder] = useState(itemOrderIds)
  const orderRef = useRef(order)
  const draggedItemID = useRef<ItemID | null>(null)

  useEffect(() => {
    orderRef.current = order
  }, [order])

  useEffect(() => {
    if (draggedItemID.current == null) setOrder(itemOrderIds)
  }, [itemOrderIds])

  const orderedItems = useMemo(
    () =>
      order
        .map((itemID) => {
          const stateIndex = itemOrderIds.indexOf(itemID)
          return stateIndex === -1 ? null : { itemID, item: items[stateIndex] }
        })
        .filter(
          (entry): entry is { itemID: ItemID; item: Item } => entry !== null
        ),
    [itemOrderIds, items, order]
  )

  const handleDragStart = useCallback((itemID: ItemID) => {
    draggedItemID.current = itemID
  }, [])

  const handleReorder = useCallback(
    (nextOrder: ItemID[]) => {
      const lockedOrder = keepDisabledItemsInPlace(
        nextOrder,
        itemOrderIds,
        disabledItemIDs
      )

      orderRef.current = lockedOrder
      setOrder(lockedOrder)
    },
    [disabledItemIDs, itemOrderIds]
  )

  const handleDragEnd = useCallback(() => {
    const draggedID = draggedItemID.current
    const nextOrder = orderRef.current
    draggedItemID.current = null
    if (draggedID == null || isEqual(nextOrder, itemOrderIds)) return

    const oldIndex = itemOrderIds.indexOf(draggedID)
    const newIndex = nextOrder.indexOf(draggedID)
    const nextItems = nextOrder.map(
      (itemID) => items[itemOrderIds.indexOf(itemID)]
    )
    onSort({ oldIndex, newIndex, items: nextItems })
  }, [itemOrderIds, items, onSort])

  return {
    order,
    orderedItems,
    handleDragStart,
    handleDragEnd,
    handleReorder
  }
}

const MotionReorderItem = <Item,>({
  itemID,
  item,
  index,
  isAtMin,
  isActive,
  isDisabled,
  onRemove,
  handleUpdateItem,
  onSetActiveIndex,
  renderItem,
  onDragStart,
  onDragEnd
}: MotionReorderItemProps<Item>) => {
  const dragControls = useDragControls()
  const suppressMenuOpen = useRef(false)
  const resetMenuTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (resetMenuTimer.current) clearTimeout(resetMenuTimer.current)
    },
    []
  )

  const MotionDragHandle = useCallback(
    ({ children }: DragHandleProps) =>
      children({
        onPointerDown: (event) => {
          if (isDisabled) return
          if (resetMenuTimer.current) clearTimeout(resetMenuTimer.current)
          suppressMenuOpen.current = false
          dragControls.start(event)
        },
        canOpen: () => !suppressMenuOpen.current
      }),
    [dragControls, isDisabled]
  )

  const handleDragEnd = () => {
    onDragEnd()
    resetMenuTimer.current = setTimeout(() => {
      suppressMenuOpen.current = false
    }, 400)
  }

  return (
    <Reorder.Item
      as="div"
      value={itemID}
      dragListener={false}
      dragControls={dragControls}
      drag="y"
      onDragStart={() => {
        suppressMenuOpen.current = true
        onDragStart(itemID)
      }}
      onDragEnd={handleDragEnd}
      style={{ listStyle: "none" }}
    >
      {renderItem({
        onRemove: () => onRemove(index),
        onUpdate: handleUpdateItem(index),
        onSetActive: () => onSetActiveIndex(index),
        item,
        isAtMin,
        isActive,
        index,
        DragHandle: MotionDragHandle
      })}
    </Reorder.Item>
  )
}

const MotionReorder = <Item,>({
  items,
  itemOrderIds,
  activeIndex,
  disabledIndices,
  autoGenerateItem,
  checkIsAutoGenerated,
  isAtMin,
  itemContainerStyles,
  renderItem,
  onRemove,
  onUpdate,
  onSetActiveIndex,
  onSort,
  children
}: MotionReorderProps<Item>) => {
  const disabledItemIDs = disabledIndices
    .map((index) => itemOrderIds[index])
    .filter((itemID): itemID is ItemID => itemID != null)
  const disabledItemIDSet = new Set(disabledItemIDs)
  const motion = useMotionReorder({
    items,
    itemOrderIds,
    disabledItemIDs,
    onSort
  })

  return (
    <Reorder.Group
      as="div"
      axis="y"
      className="ListItemContainer"
      values={motion.order}
      onReorder={motion.handleReorder}
      style={itemContainerStyles}
    >
      {motion.orderedItems.map(({ itemID, item }, index) => {
        const isDisabled =
          disabledItemIDSet.has(itemID) ||
          Boolean(autoGenerateItem && checkIsAutoGenerated?.(item))

        return (
          <MotionReorderItem
            key={itemID}
            itemID={itemID}
            item={item}
            index={index}
            isAtMin={isAtMin}
            isActive={activeIndex === index}
            isDisabled={isDisabled}
            renderItem={renderItem}
            onRemove={onRemove}
            handleUpdateItem={onUpdate}
            onSetActiveIndex={onSetActiveIndex}
            onDragStart={motion.handleDragStart}
            onDragEnd={motion.handleDragEnd}
          />
        )
      })}
      {children}
    </Reorder.Group>
  )
}

export default MotionReorder
